import React, { useState } from "react";
import {
  Label,
  Input,
  Field,
  Switch,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Dismiss20Regular, Add20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import InfoButton from "../GenericComponent/InfoButton";
import "../CSS/QueueCallback.css";

/**
 * Rappel en file d'attente — configuration par file.
 *
 * Le bloc édité ici est `queue.callback`. Il est lu par TROIS services, qui appliquent chacun
 * leurs propres replis : `UniversalQueue` (ordre de service, promesse, tentatives),
 * `FlowEngineUnify` (proposition DTMF, annonces) et `Data_Pipe` (reporting).
 *
 * UN CHAMP VIDE N'EST PAS UN ZÉRO : il est RETIRÉ du document, et le moteur applique son repli.
 * Les valeurs ci-dessous ne servent donc qu'à afficher un placeholder — on ne les écrit jamais
 * dans la file. Écrire 300 dans chaque file gèlerait le repli du jour de la création : changer
 * le défaut côté moteur ne toucherait plus aucune file existante, et personne ne comprendrait
 * pourquoi. C'est la même règle que les seuils de `Data_Pipe`.
 */
const ENGINE_DEFAULTS = {
  offerThresholdSeconds: 300,
  scheduledThresholdSeconds: 1800,
  offerDelaySeconds: 15,
  maxPendingCallbacks: 200,
  safetyMargin: 1.3,
  // Plus réglable depuis l'écran : l'annonce reborne déjà l'échéance sur les paliers
  // (waitTimeIntervals), donc le pas d'arrondi ne changeait rien à ce que le client
  // entend. Conservé ici pour deux raisons : documenter ce que le moteur applique, et
  // ne pas effacer la valeur d'un tenant qui en aurait déjà une en base.
  promiseRoundingSeconds: 300,
  fallbackAhtSeconds: 300,
  breachGuardSeconds: 120,
  maxAttempts: 3,
  expireAfterSeconds: 86400,
  dtmfKey: "1",
  interToneTimeoutSeconds: 3,
  offerSilenceTimeoutSeconds: 8,
};

/**
 * Touche enregistrée dans la bibliothèque audio commune : les annonces qui la prononcent ont été
 * synthétisées avec elle. En changer sans fournir ses propres fichiers ferait dire « tapez 1 »
 * pendant que le système attend autre chose — d'où l'avertissement, et pas un blocage.
 */
const LIBRARY_DTMF_KEY = "1";

/**
 * Paliers proposés — ce sont EXACTEMENT ceux que la bibliothèque audio contient
 * (`FlowEngineUnify/tools/callback-audio/manifest.json`, champ `intervals`).
 *
 * La liste n'est pas une saisie libre parce qu'un palier sans fichier ne produit AUCUNE erreur
 * visible : `renderPrompt` ne trouve pas d'URL, `Callback.js` journalise « audio indisponible » et
 * la proposition n'est pas jouée. Le rappel s'éteint en silence sur cette file. Une case à cocher
 * rend cette panne impossible ; un champ numérique la rendait accessible à une faute de frappe.
 */
const AUDIO_INTERVAL_STEPS = [5, 10, 15, 20, 25, 30];

/**
 * La VOIX des annonces ne se règle pas ici. Le portail ne l'offre nulle part ailleurs — ni sur la
 * musique d'attente, ni sur les messages de fallback — et l'ouvrir pour le seul rappel créerait un
 * réglage sans équivalent dans le reste de l'écran. `FlowEngineUnify` applique la voix de la
 * bibliothèque commune pour chaque langue ; `callback.voices` reste lu par le moteur et peut être
 * posé à la main sur une file qui aurait un besoin particulier.
 */

export default function QueueCallback(props) {
  const { t } = useTranslation();
  const conf = props.queue?.callback || {};
  const enabled = conf.enabled === true;

  const readOnly = props.isUpdate
    ? !props.permissions?.canUpdate
    : !props.permissions?.canCreate;
  /** Tout le reste du formulaire ne se règle que sur une file où le rappel est actif. */
  const locked = readOnly || !enabled;

  /**
   * Écrit une clé du bloc `callback`. `undefined` la SUPPRIME — c'est ce qui rend la main au
   * repli du moteur, et c'est ce que produit un champ vidé.
   */
  const setField = (key, value) => {
    const next = { ...(props.queue?.callback || {}) };
    if (value === undefined) delete next[key];
    else next[key] = value;
    props.setQueue({ ...props.queue, callback: next });
  };

  const setNumber = (key) => (_, data) => {
    const raw = String(data.value ?? "").trim();
    if (raw === "") return setField(key, undefined);
    const n = Number(raw);
    setField(key, Number.isFinite(n) ? n : undefined);
  };

  const valueOf = (key) => (conf[key] === undefined || conf[key] === null ? "" : String(conf[key]));

  const numberField = (key, { unitKey, min = 0, step, info = true } = {}) => (
    <Field
      key={key}
      className="queue-form-field"
      label={
        <>
          {t(`CALLBACK_${camelToUpper(key)}`)}
          {info && <InfoButton infoKey={`CALLBACK_${camelToUpper(key)}`} category="QUEUES" />}
        </>
      }
    >
      <Input
        type="number"
        min={min}
        step={step}
        className="queue-form-input-inline"
        placeholder={String(ENGINE_DEFAULTS[key])}
        contentAfter={unitKey ? <span className="callback-unit">{t(unitKey)}</span> : undefined}
        value={valueOf(key)}
        disabled={locked}
        onChange={setNumber(key)}
      />
    </Field>
  );

  return (
    <div className="add-queue-root callback-root">
      {/* ---------------------------------------------------------------- Activation */}
      <section className="callback-section">
        <div className="queue-form-checkbox">
          <Label weight="semibold">
            {t("CALLBACK_ENABLED")}
            <InfoButton infoKey="CALLBACK_ENABLED" category="QUEUES" />
          </Label>
          <Switch
            checked={enabled}
            disabled={readOnly}
            onChange={(_, data) => setField("enabled", data.checked)}
          />
        </div>
        {enabled && !props.queue?.channels?.call && (
          <MessageBar intent="warning" className="callback-warning">
            <MessageBarBody>{t("CALLBACK_NEEDS_CALL_CHANNEL")}</MessageBarBody>
          </MessageBar>
        )}
      </section>

      {/* ------------------------------------------------------- Quand le proposer */}
      <section className="callback-section">
        <Label weight="semibold" className="callback-section-title">
          {t("CALLBACK_SECTION_WHEN")}
          <InfoButton infoKey="CALLBACK_SECTION_WHEN" category="QUEUES" />
        </Label>
        <div className="callback-grid">
          {numberField("offerThresholdSeconds", { unitKey: "SECONDS_UNIT" })}
          {numberField("scheduledThresholdSeconds", { unitKey: "SECONDS_UNIT" })}
          {numberField("offerDelaySeconds", { unitKey: "SECONDS_UNIT" })}
          {numberField("maxPendingCallbacks")}
        </div>
      </section>

      {/* ------------------------------------------------------------ La promesse */}
      <section className="callback-section">
        <Label weight="semibold" className="callback-section-title">
          {t("CALLBACK_SECTION_PROMISE")}
          <InfoButton infoKey="CALLBACK_SECTION_PROMISE" category="QUEUES" />
        </Label>
        <div className="callback-grid">
          {numberField("safetyMargin", { min: 1, step: 0.1 })}
          {numberField("fallbackAhtSeconds", { unitKey: "SECONDS_UNIT" })}
          {numberField("breachGuardSeconds", { unitKey: "SECONDS_UNIT" })}
        </div>

        <Field
          className="queue-form-field callback-priority"
          label={
            <>
              {t("CALLBACK_PRIORITY_MODE")}
              <InfoButton infoKey="CALLBACK_PRIORITY_MODE" category="QUEUES" />
            </>
          }
        >
          <RadioGroup
            className="queue-form-radio-group"
            value={conf.priorityMode === "vipFirst" ? "vipFirst" : "promiseFirst"}
            disabled={locked}
            onChange={(_, data) => setField("priorityMode", data.value)}
          >
            <Radio value="promiseFirst" label={t("CALLBACK_PRIORITY_PROMISE_FIRST")} />
            <Radio value="vipFirst" label={t("CALLBACK_PRIORITY_VIP_FIRST")} />
          </RadioGroup>
        </Field>
      </section>

      {/* -------------------------------------------- Tentatives et durée de vie */}
      <section className="callback-section">
        <Label weight="semibold" className="callback-section-title">
          {t("CALLBACK_SECTION_RETRIES")}
          <InfoButton infoKey="CALLBACK_SECTION_RETRIES" category="QUEUES" />
        </Label>
        <div className="callback-grid">
          {numberField("maxAttempts", { min: 1 })}
          {numberField("expireAfterSeconds", { unitKey: "SECONDS_UNIT" })}
        </div>

        <ListEditor
          label={t("CALLBACK_RETRY_BACKOFF_SECONDS")}
          infoKey="CALLBACK_RETRY_BACKOFF_SECONDS"
          unit={t("SECONDS_UNIT")}
          placeholder={t("SECONDS_PLACEHOLDER")}
          values={Array.isArray(conf.retryBackoffSeconds) ? conf.retryBackoffSeconds : []}
          disabled={locked}
          canDelete={!locked}
          sorted={false}
          onChange={(list) => setField("retryBackoffSeconds", list.length ? list : undefined)}
        />
      </section>

      {/* ------------------------------------------ Ce que l'appelant entend / tape */}
      <section className="callback-section">
        <Label weight="semibold" className="callback-section-title">
          {t("CALLBACK_SECTION_CALLER")}
          <InfoButton infoKey="CALLBACK_SECTION_CALLER" category="QUEUES" />
        </Label>

        <div className="callback-grid">
          <Field
            className="queue-form-field"
            label={
              <>
                {t("CALLBACK_DTMF_KEY")}
                <InfoButton infoKey="CALLBACK_DTMF_KEY" category="QUEUES" />
              </>
            }
          >
            <Input
              className="queue-form-input-inline"
              maxLength={1}
              placeholder={LIBRARY_DTMF_KEY}
              value={conf.dtmfKey ?? ""}
              disabled={locked}
              onChange={(_, data) => {
                const raw = String(data.value ?? "").replace(/[^0-9*#]/g, "").slice(0, 1);
                setField("dtmfKey", raw === "" ? undefined : raw);
              }}
            />
          </Field>
          {numberField("interToneTimeoutSeconds", { unitKey: "SECONDS_UNIT" })}
          {numberField("offerSilenceTimeoutSeconds", { unitKey: "SECONDS_UNIT" })}
        </div>

        {conf.dtmfKey && conf.dtmfKey !== LIBRARY_DTMF_KEY && (
          <MessageBar intent="warning" className="callback-warning">
            <MessageBarBody>{t("CALLBACK_DTMF_KEY_WARNING", { key: conf.dtmfKey })}</MessageBarBody>
          </MessageBar>
        )}

        <StepPicker
          label={t("CALLBACK_WAIT_TIME_INTERVALS")}
          infoKey="CALLBACK_WAIT_TIME_INTERVALS"
          steps={AUDIO_INTERVAL_STEPS}
          unit={t("MINUTES_UNIT")}
          selected={Array.isArray(conf.waitTimeIntervals) ? conf.waitTimeIntervals.map(Number) : []}
          disabled={locked}
          emptyLabel={
            Array.isArray(props.queue?.waitTimeIntervals) && props.queue.waitTimeIntervals.length
              ? t("CALLBACK_INTERVALS_INHERITED", {
                  values: props.queue.waitTimeIntervals.join(", "),
                })
              : t("CALLBACK_INTERVALS_NONE")
          }
          onChange={(list) => setField("waitTimeIntervals", list.length ? list : undefined)}
        />

      </section>
    </div>
  );
}

/**
 * Choix de paliers parmi ceux dont l'audio existe.
 *
 * Rien de coché = clé absente = les paliers de la file s'appliquent. `emptyLabel` dit lesquels,
 * puisque cette valeur-là dépend de la file qu'on est en train d'éditer.
 */
function StepPicker(props) {
  const selected = props.selected || [];

  const toggle = (step, checked) => {
    const next = checked
      ? [...selected, step].sort((a, b) => a - b)
      : selected.filter((s) => s !== step);
    props.onChange(next);
  };

  return (
    <div className="callback-list-editor">
      <Label weight="semibold">
        {props.label}
        {props.infoKey && <InfoButton infoKey={props.infoKey} category="QUEUES" />}
      </Label>

      <div className="callback-steps">
        {props.steps.map((step) => (
          <Checkbox
            key={step}
            label={`${step} ${props.unit}`}
            checked={selected.includes(step)}
            disabled={props.disabled}
            onChange={(_, data) => toggle(step, data.checked === true)}
          />
        ))}
      </div>

      {selected.length === 0 && props.emptyLabel && (
        <span className="callback-inherited">{props.emptyLabel}</span>
      )}
    </div>
  );
}

/**
 * Éditeur de liste de nombres — paliers d'annonce, délais entre tentatives.
 *
 * Liste vide = clé absente du document = repli du moteur. `emptyLabel` est facultatif : il ne sert
 * qu'aux listes dont le repli dépend de CETTE file — les paliers, qui retombent sur ceux de la
 * file. Un repli fixe se lit dans l'infobulle du champ, il n'a pas à occuper l'écran.
 */
function ListEditor(props) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n <= 0) return;
    if (props.values.some((v) => Number(v) === n)) return setDraft("");
    const next = [...props.values.map(Number), n];
    props.onChange(props.sorted ? next.sort((a, b) => a - b) : next);
    setDraft("");
  };

  const remove = (value) => {
    props.onChange(props.values.filter((v) => Number(v) !== Number(value)));
  };

  return (
    <div className="callback-list-editor">
      <Label weight="semibold">
        {props.label}
        {props.infoKey && <InfoButton infoKey={props.infoKey} category="QUEUES" />}
      </Label>

      <div className="queue-form-wait-time-row">
        <Input
          size="small"
          type="number"
          min={1}
          placeholder={props.placeholder}
          value={draft}
          disabled={props.disabled}
          className="queue-form-table-header-input-small"
          onChange={(_, data) => setDraft(data.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button
          icon={<Add20Regular />}
          className="btn-add-style"
          appearance="primary"
          size="small"
          disabled={props.disabled || draft === ""}
          onClick={add}
        />
      </div>

      <div className="queue-form-wait-time-list-values">
        {props.values.length > 0 ? (
          props.values.map((value, index) => (
            <span key={`${value}-${index}`} className="queue-form-wait-time-tag">
              {value} {props.unit}
              {props.canDelete && (
                <Dismiss20Regular
                  className="queue-form-delete-waitTimeInterval-btn"
                  style={{ marginLeft: "4px", cursor: "pointer", verticalAlign: "middle" }}
                  onClick={() => remove(value)}
                />
              )}
            </span>
          ))
        ) : props.emptyLabel ? (
          <span className="callback-inherited">{props.emptyLabel}</span>
        ) : null}
      </div>
    </div>
  );
}

/** `offerThresholdSeconds` → `OFFER_THRESHOLD_SECONDS`, pour retrouver la clé de traduction. */
function camelToUpper(key) {
  return key.replace(/([A-Z])/g, "_$1").toUpperCase();
}
